import type { CardInput, IProvider } from '../types';

const IUGU_API = 'https://api.iugu.com/v1';
const JSONP_TIMEOUT = 40_000;

let callbackSeq = 0;

interface IuguCardData {
  number: string;
  verification_value: string;
  first_name: string;
  last_name: string;
  month: string;
  year: string;
}

export class IuguProvider implements IProvider {
  constructor(
    private readonly accountId: string,
    private readonly gatewayConfigId: string,
    /**
     * Ambiente da conta, vindo do `isTest` do `/config`. O token nasce
     * carimbado e só é cobrável pelo api_token do MESMO ambiente: errar aqui
     * tokeniza sem reclamar e só quebra na cobrança, com
     * `{"token":["is invalid"]}`.
     */
    private readonly isTest = false,
  ) {}

  async generate(card: CardInput): Promise<string> {
    const data = toCardData(card);

    // O endpoint /payment_token não devolve header CORS. No browser, a única
    // forma de chamá-lo é via JSONP (<script>), igual o iugu.js faz por baixo —
    // mas sem carregar a lib. No Node (test harness) não há CORS, então o POST
    // direto funciona. `document` só existe no browser.
    return typeof document === 'undefined'
      ? this.tokenizeViaPost(data)
      : tokenizeViaJsonp(`${IUGU_API}/payment_token`, this.accountId, data, this.isTest);
  }

  private async tokenizeViaPost(data: IuguCardData): Promise<string> {
    const response = await fetch(`${IUGU_API}/payment_token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        account_id: this.accountId,
        method: 'credit_card',
        test: this.isTest,
        data,
      }),
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Iugu tokenize failed: ${error}`);
    }

    const result = (await response.json()) as { id: string };
    return result.id;
  }
}

function toCardData(card: CardInput): IuguCardData {
  const [month, year] = card.expiration.split('/');
  const [firstName, ...rest] = card.holder.trim().split(' ');

  return {
    number: card.number.replace(/\s/g, ''),
    verification_value: card.cvv,
    first_name: firstName,
    last_name: rest.join(' ') || firstName,
    month,
    year,
  };
}

/**
 * Tokeniza via JSONP: injeta um <script> apontando pro endpoint com `?callback=`
 * e lê o token do callback global. É o que o iugu.js faz por baixo, sem carregar
 * a lib. Callback único por request (sem lock global) → N requests concorrentes
 * funcionam, ao contrário do iugu.js que serializa tudo num mutex de nome fixo.
 */
function tokenizeViaJsonp(
  url: string,
  accountId: string,
  data: IuguCardData,
  isTest: boolean,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const callback = `__iugu_cb_${Date.now()}_${++callbackSeq}`;
    const globals = window as unknown as Record<string, unknown>;
    const script = document.createElement('script');

    const cleanup = () => {
      delete globals[callback];
      clearTimeout(timer);
      script.remove();
    };

    // `timer` é usado por `cleanup` (acima) e seu callback usa `cleanup` — a
    // referência mútua é segura porque `cleanup` só roda após este bloco síncrono.
    const timer = setTimeout(() => {
      cleanup();
      reject(new Error('Iugu tokenize failed: timeout'));
    }, JSONP_TIMEOUT);

    globals[callback] = (res: { id?: string; errors?: unknown }) => {
      cleanup();
      if (res?.id && !res.errors) {
        resolve(res.id);
      } else {
        reject(new Error(`Iugu tokenize failed: ${JSON.stringify(res?.errors ?? res)}`));
      }
    };

    const params = new URLSearchParams({
      account_id: accountId,
      method: 'credit_card',
      // Explícito nos dois caminhos: omitir o campo aqui dava token de produção
      // por default, e era este o caminho que o browser usava.
      test: String(isTest),
      'data[number]': data.number,
      'data[verification_value]': data.verification_value,
      'data[first_name]': data.first_name,
      'data[last_name]': data.last_name,
      'data[month]': data.month,
      'data[year]': data.year,
      callback,
    });

    script.async = true;
    script.src = `${url}?${params.toString()}`;
    script.onerror = () => {
      cleanup();
      reject(new Error('Iugu tokenize failed: script load error'));
    };
    document.head.appendChild(script);
  });
}
